import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { IRunningStateInfo } from 'src/app/components/wizard/steps/general-task-configuration-step/general-task-configuration-step.component';
import { IThingieCreationContext } from 'src/app/model/thingie-creation-wizard/thingie-creation-context';
import { IApplicationTemplateMetadata, ITaskTemplateMetadata } from 'src/app/model/thingie/templates';

export type ThingieWizardContextHandlerType = () => any;

export type CommitOptions = 'thingies' | 'template' | 'thingiesAndTemplate' | 'thingiesAndStart' | 'thingiesAndUpload';

export interface FormControlsToValidate {
  baseThingieName: FormControl<any> | undefined;
  replicateCount: FormControl<any> | undefined;
}

/**
 * Utility service to hold the thingie wizard's
 * context instance.
 *
 * Initialized with a default context.
 */
@Injectable({
  providedIn: null,
})
export abstract class ThingieWizardContextService {
  public abstract get context$(): Observable<IThingieCreationContext>;

  /**
   * Update the context
   *
   * @param context
   */
  public abstract update(context: IThingieCreationContext): void;

  /**
   * Re-sets the context to the default context.
   */
  public abstract reset(): void;

  /**
   * Observable which emits, when the configuration
   * shall be committed (leading to (batch) thingie creation/update).
   */
  public abstract get commitChanges$(): Observable<CommitOptions>;

  /**
   * Observable which emits when the wizard
   * is to be closed without committing changes
   * made to it's context.
   */
  public abstract get quitTriggers$(): Observable<void>;

  /**
   * Observable which emits when the
   * the wizard changes to another step.
   */
  public abstract get stepChange$(): Observable<StepperSelectionEvent>;

  /**
   * Commit all changes
   */
  public abstract commitChanges(option: CommitOptions): void;

  /**
   * Request to close the wizard
   * without commiting changes made to it's context.
   */
  public abstract quitWizard(): void;

  /**
   * Notifies listeners of a step-change event.
   *
   * @param $event
   */
  public abstract notifyOfStepChange($event: StepperSelectionEvent): void;

  /**
   * Notifies listeners if we are in a loading state,
   * where no thingie-adjusting user interactions should be allowed.
   */
  public abstract get isLoading$(): Observable<boolean>;

  /**
   * Notify listeners of {@link isLoading$}.
   *
   * @see {@link isLoading$}
   */
  public abstract showLoading(state: boolean): void;


  public abstract setFormControlToValidate(key: keyof FormControlsToValidate, formControl: FormControl<any>): void;
  public abstract getFormControlToValidate(key: keyof FormControlsToValidate): FormControl<any> | undefined;


  /**
   * Generates the running state info of the thingies in the context.
   *
   * @returns an object with two variants of the result:
   * - the general task configuration step (without duplicated tasks)
   * - the replicate configuration step (with duplicated tasks).
   */
  public abstract determineRunningStateInfo(): { full: IRunningStateInfo; withoutDuplicates: IRunningStateInfo };

  /**
   * tracks a user changing a setting in the wizard
   */
  public abstract get configChange$(): Observable<void>;

  /**
   * track a user changing a setting in the wizard
   */
  public abstract notifyOfConfigChange(): void;


  /**
   * Notify interested parties of a change in the experiment name
   *
   * @see {@link experimentNameChange$}
   */
  public abstract notifyOfExperimentNameChange(name: string): void;


  /**
   * Observable which emits when the experiment name changes
   *
   * @see {@link notifyOfExperimentNameChange}
   */
  public abstract get experimentNameChange$(): Observable<string>;

  /**
   * Obsverables for template metadata
   */
  public abstract get taskTemplateMetadata$(): Observable<ITaskTemplateMetadata[]>;
  public abstract get appTemplateMetadata$(): Observable<IApplicationTemplateMetadata[]>;
}
